import { useEffect, useState } from "react";
import { listBuddyGuarantees } from "./buddyGuaranteeApi.js";
import { sourceKey } from "./buddyGuaranteeShared.js";

/**
 * Prompt 140B-R1: merges real buddy-guarantee data onto a roster page of
 * real people, by source person id/type - one lightweight, real
 * `GET .../buddy-guarantees?workerApplicationId=...&buddyStatus=Active&pageSize=1`
 * (or `workerProfileId=`) call per candidate, reading only the response's
 * own real `totalCount` (never its `items`) as that person's real active-
 * buddy count. Bounded by however many candidates the caller passes in -
 * always one roster PAGE (e.g. 20), never the platform-wide person count -
 * so this stays cheap and real even with thousands of people in scope.
 *
 * The caller must pass a candidates array that only changes reference when
 * its *contents* actually change (the roster hook's own page/search
 * re-fetch already does this naturally) - this effect keys off that same
 * array reference.
 */
export function useBuddyActiveCounts(candidates) {
  const [state, setState] = useState({ status: "idle", counts: {} });

  useEffect(() => {
    if (!candidates || candidates.length === 0) {
      setState({ status: "ready", counts: {} });
      return undefined;
    }

    let cancelled = false;
    setState((previous) => ({ status: "loading", counts: previous.counts }));

    Promise.all(
      candidates.map((candidate) =>
        listBuddyGuarantees({
          workerApplicationId: candidate.workerApplicationId ?? undefined,
          workerProfileId: candidate.workerProfileId ?? undefined,
          buddyStatus: "Active",
          pageSize: 1,
        })
          .then((data) => [sourceKey(candidate), data?.totalCount ?? 0])
          .catch(() => [sourceKey(candidate), null])
      )
    ).then((entries) => {
      if (!cancelled) {
        setState({ status: "ready", counts: Object.fromEntries(entries) });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [candidates]);

  /** Overwrites one candidate's known count directly (e.g. with the exact fresh value a create/update/revoke response just returned) - no extra network call. */
  function setCount(candidate, count) {
    setState((previous) => ({ ...previous, counts: { ...previous.counts, [sourceKey(candidate)]: count } }));
  }

  return { status: state.status, counts: state.counts, setCount };
}
